<script setup lang="ts">
import { inject, ref, watch } from 'vue';
import { UserInRoom, getUserById } from '../states/usersState';
import { locale } from '../locale';
import { assignAdminRequest, isAdmin, revokeAdminRequest } from '../states/adminsState';
import useClickOutside from '../composables/useClickOutside';
import { adminChangeMicroState } from '../actions/AdminChangeMicroState';
import { adminKickUserInRoom } from '../actions/AdminKickUserInRoom';
import { adminHandDown, isHandUp } from '../states/handUpState';
import { stopAdminScreenShare, userIsScreenShared } from '../states/screenShareState';
import { revokeUserEnter } from '../states/waitRoomState';
import { createPopper } from '@popperjs/core';
import { roomState } from '../states/room';

const props = defineProps<{
  user: UserInRoom;
  control: HTMLElement | null
  focused?: boolean;
}>();
const popup = ref<HTMLElement | null>(null)
const opened = inject('opened-controls', ref(false))
watch(() => opened.value && !!popup.value && !!props.control, (next) => {
  if(next && props.control && popup.value) {
    createPopper(props.control, popup.value, {
      placement: "bottom-start"
    })
  }
})
useClickOutside(popup, opened);
const changeMicroState = () => {
  adminChangeMicroState(props.user.id);
  setTimeout(() => {
    opened.value = false;
  }, 100);
};
const kickUser = () => {
  adminKickUserInRoom(props.user.id);
  setTimeout(() => {
    opened.value = false;
  }, 100);
};
const adminAssign = async () => {
  await assignAdminRequest(props.user.id);
  setTimeout(() => {
    opened.value = false;
  }, 100);
};

const adminRevoke = async () => {
  await revokeAdminRequest(props.user.id);
  setTimeout(() => {
    opened.value = false;
  }, 100);
};

const handDown = async () => {
  await adminHandDown(props.user.id);
  setTimeout(() => {
    opened.value = false;
  }, 100);
};
const demoOff = async () => {
  await stopAdminScreenShare(props.user.id)
  setTimeout(() => {
    opened.value = false;
  }, 100);
};
const toWait = async () => {
  await revokeUserEnter(props.user.id);
  setTimeout(() => {
    opened.value = false;
  }, 100);
};
</script>
<template>
    <Teleport to="body">
      <div v-if="opened" ref="popup" class="user-controls">
        <div
          v-if="getUserById(user.id)?.micro === true"
          class="control-item"
          @click="changeMicroState()"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 8.4V9.2C5 10.6852 5.39981 12.0498 6.4 13.1C7.40019 14.1502 8.58551 14.8 10 14.8M10 14.8C11.4145 14.8 12.7498 14.0502 13.75 13C13.7873 12.9608 13.9563 13.081 13.9924 13.0408M10 14.8V18M15 8.4V9.2C15 9.72364 14.9843 10.0052 14.85 10.5M10 18H8.75M10 18H11.4444M3 2L11.7912 10.8273M17 16L13.9924 13.0408M7.8254 6.84524V9.2C7.8254 10.5255 8.73763 11.6 10 11.6C10.6643 11.6 11.3735 11.3024 11.7912 10.8273M11.7912 10.8273L13.607 12.6506L13.9924 13.0408M12.15 7.77778V4.4C12.15 3.05 11.1389 2 10 2C8.75 2 8.2 3 8.04062 3.38197"
              stroke="white"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="control-text">{{ locale.voiceoff }}</span>
        </div>
        <div v-if="!isAdmin(user.id)" class="control-item" @click="adminAssign">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.35294 16H15.6471M4.35294 13.3333L2 5.33333L6.23529 8.44444L10 4L13.7647 8.44444L18 5.33333L15.6471 13.3333H4.35294Z"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="control-text">{{
            locale.userControls.adminAssignee
          }}</span>
        </div>
        <div v-if="isAdmin(user.id)" class="control-item" @click="adminRevoke">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16 16.8433L2.25836 3.10162"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M4.35294 13.3338L3.63342 13.5454C3.72729 13.8646 4.02023 14.0838 4.35294 14.0838V13.3338ZM10 4.00049L10.5723 3.51573C10.4298 3.3475 10.2205 3.25049 10 3.25049C9.77952 3.25049 9.57022 3.3475 9.42771 3.51573L10 4.00049ZM13.7647 8.44493L13.1924 8.92969C13.4471 9.23039 13.8911 9.28268 14.2087 9.04938L13.7647 8.44493ZM18 5.33382L18.7195 5.54545C18.8103 5.23676 18.6941 4.90456 18.4307 4.71979C18.1672 4.53503 17.8153 4.53889 17.556 4.72937L18 5.33382ZM14.9275 13.1222C14.8107 13.5196 15.038 13.9365 15.4354 14.0533C15.8328 14.1702 16.2497 13.9428 16.3666 13.5454L14.9275 13.1222ZM4.35294 15.2505C3.93873 15.2505 3.60294 15.5863 3.60294 16.0005C3.60294 16.4147 3.93873 16.7505 4.35294 16.7505V15.2505ZM11.7765 16.7505C12.1907 16.7505 12.5265 16.4147 12.5265 16.0005C12.5265 15.5863 12.1907 15.2505 11.7765 15.2505V16.7505ZM3.09648 6.40384C2.9796 6.00646 2.56271 5.77907 2.16533 5.89594C1.76795 6.01282 1.54055 6.42971 1.65743 6.82709L3.09648 6.40384ZM9 14.0838C9.41421 14.0838 9.75 13.748 9.75 13.3338C9.75 12.9196 9.41421 12.5838 9 12.5838V14.0838ZM9.42771 4.48525L13.1924 8.92969L14.337 7.96017L10.5723 3.51573L9.42771 4.48525ZM14.2087 9.04938L18.444 5.93827L17.556 4.72937L13.3207 7.84049L14.2087 9.04938ZM17.2805 5.1222L14.9275 13.1222L16.3666 13.5454L18.7195 5.54545L17.2805 5.1222ZM4.35294 16.7505H11.7765V15.2505H4.35294V16.7505ZM5.07246 13.1222L3.09648 6.40384L1.65743 6.82709L3.63342 13.5454L5.07246 13.1222ZM7.36148 8.27578L10.5723 4.48525L9.42771 3.51573L6.21691 7.30626L7.36148 8.27578ZM9 12.5838H4.35294V14.0838H9V12.5838Z"
              fill="white"
            />
          </svg>
          <span class="control-text">{{
            locale.userControls.adminRevoke
          }}</span>
        </div>
        <div v-if="isHandUp(user.id)" class="control-item" @click="handDown">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.6465 17.707L4.55192 4.61246"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M13.9553 7.56615L14.4553 7.56842L14.4553 7.56572L13.9553 7.56615ZM13.9413 10.6411L13.4413 10.6392C13.4403 10.9153 13.6632 11.1399 13.9392 11.141C14.2153 11.1422 14.44 10.9194 14.4413 10.6433L13.9413 10.6411ZM13.9627 4.20733L14.4627 4.20844L14.4627 4.20776L13.9627 4.20733ZM12.7754 3.01999V3.52L12.7758 3.51999L12.7754 3.01999ZM11.588 4.20734L12.088 4.2081V4.20734H11.588ZM11.5768 10.6411L11.0768 10.6402C11.0764 10.9163 11.2998 11.1406 11.576 11.141C11.8521 11.1415 12.0763 10.9181 12.0768 10.6419L11.5768 10.6411ZM11.5897 3.08773L12.0897 3.08858V3.08816L11.5897 3.08773ZM10.4024 1.90039V2.40039L10.4028 2.40039L10.4024 1.90039ZM9.21503 3.08773L9.71503 3.0882V3.08773H9.21503ZM9.20551 9.10464L8.70552 9.10372C8.70501 9.3798 8.92835 9.60404 9.20443 9.60464C9.4805 9.60524 9.70482 9.38198 9.70551 9.1059L9.20551 9.10464ZM9.21503 5.32674L9.71503 5.328L9.71503 5.32717L9.21503 5.32674ZM8.02769 4.1394V4.6394L8.02812 4.6394L8.02769 4.1394ZM6.82355 12.7204L6.47005 13.074C6.61306 13.217 6.82811 13.2598 7.01493 13.1824C7.20175 13.105 7.32355 12.9227 7.32355 12.7204H6.82355ZM5.10256 10.9999L4.74901 11.3535L4.74906 11.3535L5.10256 10.9999ZM2.86335 13.2391L2.5098 13.5927H2.5098L2.86335 13.2391ZM16.3165 13.2692L15.8165 13.268L15.8165 13.2691L16.3165 13.2692ZM16.3305 7.56671L16.8305 7.56794V7.56671H16.3305ZM7.32355 10.1086C7.32355 9.83248 7.09969 9.60863 6.82355 9.60863C6.54741 9.60863 6.32355 9.83248 6.32355 10.1086H7.32355ZM6.34035 6.85511C6.34035 7.13126 6.5642 7.35511 6.84035 7.35511C7.11649 7.35511 7.34035 7.13126 7.34035 6.85511H6.34035ZM14.5202 18.0391C14.7459 17.88 14.7999 17.568 14.6408 17.3423C14.4817 17.1166 14.1698 17.0626 13.9441 17.2217L14.5202 18.0391ZM13.4553 7.56387L13.4413 10.6388L14.4413 10.6433L14.4553 7.56842L13.4553 7.56387ZM14.4627 4.20776C14.4635 3.27576 13.7069 2.5192 12.7749 2.52L12.7758 3.51999C13.1548 3.51967 13.463 3.82786 13.4627 4.20691L14.4627 4.20776ZM12.7754 2.51999C11.8431 2.51999 11.088 3.2751 11.088 4.20734H12.088C12.088 3.82739 12.3954 3.51999 12.7754 3.51999V2.51999ZM12.0768 10.6419L12.0897 3.08858L11.0897 3.08688L11.0768 10.6402L12.0768 10.6419ZM12.0897 3.08816C12.0905 2.15615 11.334 1.3996 10.4019 1.40039L10.4028 2.40039C10.7819 2.40007 11.09 2.70825 11.0897 3.0873L12.0897 3.08816ZM10.4024 1.40039C9.47014 1.40039 8.71503 2.1555 8.71503 3.08773H9.71503C9.71503 2.70779 10.0224 2.40039 10.4024 2.40039V1.40039ZM9.70551 9.1059L9.71503 5.328L8.71503 5.32548L8.70552 9.10338L9.70551 9.1059ZM9.71503 5.32717C9.71583 4.39516 8.95927 3.63861 8.02726 3.6394L8.02812 4.6394C8.40717 4.63908 8.71536 4.94726 8.71503 5.32632L9.71503 5.32717ZM8.02769 3.6394C7.09546 3.6394 6.34035 4.39451 6.34035 5.32674H7.34035C7.34035 4.9468 7.64774 4.6394 8.02769 4.6394V3.6394ZM7.17706 12.3668L5.45607 10.6463L4.74906 11.3535L6.47005 13.074L7.17706 12.3668ZM5.45612 10.6464C4.64227 9.83254 3.32364 9.83254 2.5098 10.6464L3.2169 11.3535C3.64022 10.9302 4.32569 10.9302 4.74901 11.3535L5.45612 10.6464ZM2.5098 10.6464C1.69595 11.4602 1.69595 12.7789 2.5098 13.5927L3.2169 12.8856C2.79358 12.4623 2.79358 11.7768 3.2169 11.3535L2.5098 10.6464ZM16.8165 13.2705L16.8305 7.56794L15.8305 7.56548L15.8165 13.268L16.8165 13.2705ZM16.8305 7.56671C16.8305 6.63448 16.0754 5.87937 15.1432 5.87937V6.87937C15.5231 6.87937 15.8305 7.18676 15.8305 7.56671H16.8305ZM15.1432 5.87937C14.2118 5.87937 13.4545 6.63372 13.4553 7.56658L14.4553 7.56572C14.4549 7.18752 14.7624 6.87937 15.1432 6.87937V5.87937ZM13.9413 10.6411C14.4413 10.6429 14.4413 10.6429 14.4413 10.6428C14.4413 10.6428 14.4413 10.6427 14.4413 10.6426C14.4413 10.6425 14.4413 10.6423 14.4413 10.642C14.4413 10.6413 14.4413 10.6404 14.4413 10.6392C14.4413 10.6368 14.4413 10.6332 14.4413 10.6284C14.4414 10.6189 14.4414 10.6047 14.4415 10.5861C14.4416 10.549 14.4418 10.4944 14.4421 10.4243C14.4426 10.2843 14.4433 10.0826 14.4442 9.83616C14.4459 9.34331 14.4483 8.67146 14.4508 7.95549C14.4558 6.5237 14.4611 4.9149 14.4627 4.20844L13.4627 4.20623C13.4611 4.91192 13.4558 6.51999 13.4508 7.95202C13.4483 8.66795 13.446 9.33975 13.4442 9.83258C13.4433 10.079 13.4426 10.2807 13.4421 10.4207C13.4418 10.4907 13.4416 10.5454 13.4415 10.5825C13.4414 10.6011 13.4414 10.6152 13.4413 10.6248C13.4413 10.6296 13.4413 10.6332 13.4413 10.6356C13.4413 10.6368 13.4413 10.6377 13.4413 10.6383C13.4413 10.6386 13.4413 10.6388 13.4413 10.639C13.4413 10.6391 13.4413 10.6391 13.4413 10.6392C13.4413 10.6392 13.4413 10.6392 13.9413 10.6411ZM11.088 4.20657C11.087 4.90158 11.0842 6.50994 11.0816 7.94462C11.0804 8.66195 11.0792 9.33583 11.0783 9.83038C11.0778 10.0777 11.0775 10.2801 11.0772 10.4207C11.0771 10.491 11.077 10.5459 11.0769 10.5832C11.0769 10.6018 11.0769 10.6161 11.0769 10.6256C11.0769 10.6304 11.0769 10.6341 11.0768 10.6365C11.0768 10.6377 11.0768 10.6386 11.0768 10.6392C11.0768 10.6395 11.0768 10.6398 11.0768 10.6399C11.0768 10.64 11.0768 10.6401 11.0768 10.6401C11.0768 10.6401 11.0768 10.6402 11.5768 10.6411C12.0768 10.6419 12.0768 10.6419 12.0768 10.6419C12.0768 10.6419 12.0768 10.6418 12.0768 10.6417C12.0768 10.6416 12.0768 10.6413 12.0768 10.641C12.0768 10.6404 12.0768 10.6395 12.0768 10.6383C12.0768 10.6359 12.0769 10.6322 12.0769 10.6274C12.0769 10.6179 12.0769 10.6036 12.0769 10.585C12.077 10.5477 12.0771 10.4928 12.0772 10.4225C12.0775 10.2819 12.0778 10.0794 12.0783 9.83217C12.0792 9.33761 12.0804 8.66372 12.0816 7.94639C12.0842 6.51175 12.087 4.90325 12.088 4.2081L11.088 4.20657ZM8.71503 3.08727C8.71425 3.93484 8.71187 5.43888 8.70968 6.73119C8.70859 7.37731 8.70755 7.97045 8.70678 8.40205C8.7064 8.61786 8.70608 8.79327 8.70586 8.91474C8.70575 8.97548 8.70566 9.02272 8.70561 9.05479C8.70558 9.07082 8.70555 9.08305 8.70554 9.09128C8.70553 9.09539 8.70553 9.0985 8.70552 9.10059C8.70552 9.10163 8.70552 9.10241 8.70552 9.10294C8.70552 9.1032 8.70552 9.1034 8.70552 9.10353C8.70552 9.10359 8.70552 9.10364 8.70552 9.10368C8.70552 9.10371 8.70552 9.10372 9.20551 9.10464C9.70551 9.10556 9.70551 9.10555 9.70551 9.10551C9.70551 9.10548 9.70551 9.10543 9.70551 9.10537C9.70551 9.10523 9.70552 9.10504 9.70552 9.10477C9.70552 9.10425 9.70552 9.10347 9.70552 9.10242C9.70552 9.10034 9.70553 9.09723 9.70554 9.09311C9.70555 9.08488 9.70557 9.07265 9.7056 9.05661C9.70566 9.02455 9.70575 8.9773 9.70586 8.91656C9.70608 8.79508 9.7064 8.61965 9.70678 8.40383C9.70755 7.97221 9.70859 7.37904 9.70968 6.73288C9.71187 5.44063 9.71425 3.93621 9.71503 3.0882L8.71503 3.08727ZM2.5098 13.5927C2.97061 14.0535 3.45493 14.6006 4.01937 15.2029C4.57434 15.7951 5.18842 16.4201 5.86959 16.9886C7.23151 18.1255 8.89751 19.0671 10.9564 19.0671V18.0671C9.22436 18.0671 7.77947 17.2803 6.5104 16.221C5.87607 15.6915 5.29464 15.1013 4.74905 14.5191C4.21295 13.9471 3.6917 13.3604 3.2169 12.8856L2.5098 13.5927ZM6.32355 10.1086V12.7204H7.32355V10.1086H6.32355ZM6.34035 5.32674V6.85511H7.34035V5.32674H6.34035ZM16.1944 16.0537C16.6207 15.1803 16.8162 14.211 16.8165 13.2694L15.8165 13.2691C15.8163 14.0767 15.648 14.8933 15.2957 15.615L16.1944 16.0537ZM10.9564 19.0671C12.4195 19.0671 13.6047 18.6843 14.5202 18.0391L13.9441 17.2217C13.2174 17.7339 12.2394 18.0671 10.9564 18.0671V19.0671Z"
              fill="white"
            />
          </svg>
          <span class="control-text">{{ locale.userControls.handDown }}</span>
        </div>
        <div v-if="userIsScreenShared(user.id)" class="control-item" @click="demoOff">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 15.5V4.5C1 3.39543 1.89543 2.5 3 2.5H17C18.1046 2.5 19 3.39543 19 4.5V15.5C19 16.6046 18.1046 17.5 17 17.5H10H3C1.89543 17.5 1 16.6046 1 15.5Z"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M7 7.5L13 12.5M7 12.5L13 7.5"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          <span class="control-text">{{ locale.userControls.demoOff }}</span>
        </div>
        <div v-if="roomState.waitRoom && !isAdmin(user.id)" class="control-item" @click="toWait">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.64548 10.6054L8.89548 10.6039C8.89492 10.8845 9.05105 11.1419 9.30014 11.2711L9.64548 10.6054ZM10.4041 6.28254C10.4049 5.86832 10.0698 5.53187 9.65561 5.53104C9.2414 5.53021 8.90494 5.86533 8.90411 6.27954L10.4041 6.28254ZM11.3844 12.3522C11.7521 12.543 12.2047 12.3995 12.3955 12.0318C12.5862 11.6641 12.4427 11.2115 12.075 11.0207L11.3844 12.3522ZM17.25 10C17.25 14.0041 14.0041 17.25 10 17.25V18.75C14.8325 18.75 18.75 14.8325 18.75 10H17.25ZM10 17.25C5.99594 17.25 2.75 14.0041 2.75 10H1.25C1.25 14.8325 5.16751 18.75 10 18.75V17.25ZM2.75 10C2.75 5.99594 5.99594 2.75 10 2.75V1.25C5.16751 1.25 1.25 5.16751 1.25 10H2.75ZM10 2.75C14.0041 2.75 17.25 5.99594 17.25 10H18.75C18.75 5.16751 14.8325 1.25 10 1.25V2.75ZM10.3955 10.6069L10.4041 6.28254L8.90411 6.27954L8.89548 10.6039L10.3955 10.6069ZM9.30014 11.2711L11.3844 12.3522L12.075 11.0207L9.99082 9.9396L9.30014 11.2711Z"
              fill="white"
            />
          </svg>
          <span class="control-text">{{ locale.userControls.toWait }}</span>
        </div>
        <div class="control-item" @click="kickUser">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M8.92269 10.2826C10.9779 10.2826 12.6225 8.64052 12.6225 6.64128C12.6225 4.64203 10.9779 3 8.92269 3C6.86748 3 5.22287 4.64203 5.22287 6.64128C5.22287 8.64052 6.86748 10.2826 8.92269 10.2826ZM8.92256 17.6636C9.43901 17.6636 9.92463 17.6385 10.3782 17.594C10.1525 17.3019 9.96113 16.9806 9.81037 16.6364C9.52743 16.654 9.2314 16.6636 8.92256 16.6636C7.26809 16.6636 5.98149 16.3897 5.104 16.0695C4.6634 15.9088 4.34091 15.7418 4.13147 15.6034C4.07581 15.5666 4.03354 15.5355 4.00206 15.5106C4.00611 15.4279 4.01642 15.3204 4.03541 15.19C4.08585 14.8437 4.18642 14.4108 4.32524 13.9906C4.46686 13.562 4.63014 13.2021 4.78451 12.9709C4.80677 12.9375 4.82648 12.9102 4.84355 12.8879C4.96164 12.9232 5.14332 13.0014 5.47474 13.1605L5.90765 12.2591L5.47474 13.1605L5.4814 13.1637C6.21303 13.5151 7.26909 14.0223 8.92256 14.0223C9.11945 14.0223 9.30787 14.0151 9.48828 14.0018C9.54912 13.6399 9.65176 13.2927 9.7909 12.9656C9.52503 13.0015 9.23682 13.0223 8.92256 13.0223C7.4968 13.0223 6.60724 12.5951 5.90765 12.2591C5.45427 12.0414 5.08067 11.862 4.69261 11.862C3.63526 11.862 2.7866 15.348 3.04764 15.9231C3.30867 16.4981 5.39787 17.6636 8.92256 17.6636ZM4.00292 15.6533C4.00283 15.6534 4.00239 15.6507 4.00188 15.6447C4.00276 15.6502 4.00301 15.6532 4.00292 15.6533ZM4.92395 12.7999C4.92399 12.8003 4.92029 12.8037 4.91273 12.8087C4.92014 12.8019 4.92391 12.7994 4.92395 12.7999ZM13.6225 6.64128C13.6225 9.20458 11.5183 11.2826 8.92269 11.2826C6.32705 11.2826 4.22287 9.20458 4.22287 6.64128C4.22287 4.07797 6.32705 2 8.92269 2C11.5183 2 13.6225 4.07797 13.6225 6.64128ZM14.583 17.3333C14.0935 17.3333 13.6338 17.2053 13.2356 16.9811L16.6983 12.8259C17.0947 13.3024 17.333 13.915 17.333 14.5833C17.333 16.102 16.1018 17.3333 14.583 17.3333ZM12.4677 16.3406L15.9304 12.1854C15.5322 11.9612 15.0725 11.8333 14.583 11.8333C13.0642 11.8333 11.833 13.0645 11.833 14.5833C11.833 15.2515 12.0714 15.8641 12.4677 16.3406ZM18.333 14.5833C18.333 16.6543 16.6541 18.3333 14.583 18.3333C12.5119 18.3333 10.833 16.6543 10.833 14.5833C10.833 12.5122 12.5119 10.8333 14.583 10.8333C16.6541 10.8333 18.333 12.5122 18.333 14.5833Z"
              fill="white"
            />
          </svg>
          <span class="control-text">{{ locale.kick }}</span>
        </div>
      </div>
    </Teleport>
</template>
<style lang="scss" scoped>
.control-item:hover {
  background-color: #494949;
}

.user-controls {
  position: fixed;
  width: 292px;
  /* height: 120px; */
  padding: 8px 0;
  border-radius: 7px;
  border: 1px solid #494949;
  background: rgba(41, 41, 41, 0.75);
  backdrop-filter: blur(12.5px);
  left: 0;
  top: 0;
  z-index: 200;
}

.control-item {
  display: flex;
  align-items: center;
  width: 100%;
  height: 52px;
  padding: 16px 12px;
  gap: 12px;
  cursor: pointer;
}

.control-item .control-text {
  color: #fff;
  font-family: Open Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}
  
</style>
